import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Mic, Play, Pause, ExternalLink, Youtube, Headphones, 
  Clock, Calendar, Search, Filter, X, ChevronDown,
  ArrowLeft, Loader2
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { fetchPodcastEpisodes } from '../services/podcastService'
import ecosystem from '../data/ecosystem.json'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
}

export default function PodcastPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEpisode, setSelectedEpisode] = useState(null)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'

  const { data: episodes, isLoading, error } = useQuery({
    queryKey: ['podcast-episodes'],
    queryFn: fetchPodcastEpisodes,
  })

  const { podcast } = ecosystem.media

  // Filter episodes based on search
  const filteredEpisodes = useMemo(() => {
    if (!episodes) return []
    if (!searchQuery.trim()) return episodes
    
    const query = searchQuery.toLowerCase()
    return episodes.filter(
      (ep) =>
        ep.title.toLowerCase().includes(query) ||
        ep.description?.toLowerCase().includes(query)
    )
  }, [episodes, searchQuery])

  // Format duration from milliseconds
  const formatDuration = (ms) => {
    if (!ms) return '--'
    const minutes = Math.floor(ms / 60000)
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return `${hours}h ${mins}m`
    }
    return `${minutes} min`
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '--'
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  return (
    <main className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-gradient-to-br from-rose-valley-900 via-rose-valley-800 to-primary-900 lg:py-24">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-rose-valley-500/20 blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-primary-500/20 blur-3xl" />
        
        <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 mb-8 text-sm font-medium text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-start gap-8 lg:flex-row lg:items-center"
          >
            <div className="w-32 h-32 rounded-3xl overflow-hidden shadow-2xl shadow-rose-valley-900/50">
              <img
                src="/media/cover.jpg"
                alt={podcast.name}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-white sm:text-5xl">
                {podcast.name}
              </h1>
              <p className="mt-2 text-xl text-rose-valley-200">{podcast.tagline}</p>
              <p className="max-w-2xl mt-4 text-white/70">{podcast.description}</p>
              
              {/* Platform Links */}
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <a
                  href={podcast.platforms.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-full bg-white/10 hover:bg-white/20"
                >
                  <Youtube className="w-4 h-4" />
                  YouTube
                </a>
                <a
                  href={podcast.platforms.spotify}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-full bg-white/10 hover:bg-white/20"
                >
                  <Headphones className="w-4 h-4" />
                  Spotify
                </a>
                <a
                  href={podcast.platforms.apple}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-full bg-white/10 hover:bg-white/20"
                >
                  <Mic className="w-4 h-4" />
                  Apple Podcasts
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Episodes Section */}
      <section className="py-12 lg:py-16">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Search & Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="relative flex-1 max-w-md">
              <Search className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
              <input
                type="text"
                placeholder="Rechercher un épisode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full py-3 pl-12 pr-4 text-sm transition-colors bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-valley-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute p-1 text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {filteredEpisodes.length} épisode{filteredEpisodes.length > 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-rose-valley-500 animate-spin" />
              <p className="mt-4 text-gray-500">Chargement des épisodes...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-lg font-medium text-gray-900">Impossible de charger les épisodes pour le moment</p>
              <p className="mt-2 text-gray-500">Retrouvez tous nos épisodes directement sur vos plateformes préférées :</p>
              
              <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                <a
                  href={podcast.platforms.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-full bg-[#FF0000] hover:bg-[#FF0000]/90"
                >
                  <Youtube className="w-4 h-4" />
                  YouTube
                </a>
                <a
                  href={podcast.platforms.spotify}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-full bg-[#1DB954] hover:bg-[#1DB954]/90"
                >
                  <Headphones className="w-4 h-4" />
                  Spotify
                </a>
                <a
                  href={podcast.platforms.apple}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-full bg-[#872EC4] hover:bg-[#872EC4]/90"
                >
                  <Mic className="w-4 h-4" />
                  Apple Podcasts
                </a>
              </div>
            </div>
          )}

          {/* Episodes Grid */}
          {!isLoading && !error && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className={
                viewMode === 'grid'
                  ? 'grid gap-6 sm:grid-cols-2 lg:grid-cols-3'
                  : 'space-y-4'
              }
            >
              <AnimatePresence mode="popLayout">
                {filteredEpisodes.map((episode, index) => (
                  <motion.article
                    key={episode.id || index}
                    variants={itemVariants}
                    layout
                    whileHover={{ y: -4 }}
                    className={`group relative overflow-hidden bg-white border border-gray-100 shadow-sm transition-all hover:shadow-lg ${
                      viewMode === 'grid' ? 'rounded-2xl' : 'rounded-xl flex items-center gap-4 p-4'
                    }`}
                  >
                    {viewMode === 'grid' ? (
                      <>
                        {/* Episode Thumbnail */}
                        <div className="relative aspect-square bg-black overflow-hidden">
                          <img
                            src={episode.artworkUrl600 || '/media/cover.jpg'}
                            alt={episode.title}
                            className="object-contain w-full h-full transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 flex items-center justify-center transition-opacity bg-black/40 opacity-0 group-hover:opacity-100">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setSelectedEpisode(episode)}
                              className="flex items-center justify-center w-14 h-14 text-white rounded-full bg-rose-valley-600"
                            >
                              <Play className="w-6 h-6 ml-1" />
                            </motion.button>
                          </div>
                        </div>

                        {/* Episode Info */}
                        <div className="p-5">
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatDate(episode.releaseDate)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {formatDuration(episode.trackTimeMillis)}
                            </span>
                          </div>
                          
                          <h3 className="mt-2 font-semibold text-gray-900 line-clamp-2 group-hover:text-rose-valley-700 transition-colors">
                            {episode.trackName || episode.title}
                          </h3>
                          
                          <p className="mt-2 text-sm text-gray-500 line-clamp-2">
                            {episode.description?.replace(/<[^>]*>/g, '').slice(0, 150)}...
                          </p>

                          <div className="flex items-center gap-2 mt-4">
                            <button
                              onClick={() => setSelectedEpisode(episode)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors rounded-full bg-rose-valley-50 text-rose-valley-700 hover:bg-rose-valley-100"
                            >
                              <Play className="w-3.5 h-3.5" />
                              Écouter
                            </button>
                            {episode.episodeUrl && (
                              <a
                                href={episode.episodeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                Ouvrir
                              </a>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      // List View
                      <>
                        <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-black">
                          <img
                            src={episode.artworkUrl600 || '/media/cover.jpg'}
                            alt={episode.title}
                            className="object-cover w-full h-full"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate group-hover:text-rose-valley-700 transition-colors">
                            {episode.trackName || episode.title}
                          </h3>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                            <span>{formatDate(episode.releaseDate)}</span>
                            <span>{formatDuration(episode.trackTimeMillis)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedEpisode(episode)}
                          className="flex items-center justify-center w-10 h-10 transition-colors rounded-full bg-rose-valley-100 text-rose-valley-600 hover:bg-rose-valley-200"
                        >
                          <Play className="w-5 h-5 ml-0.5" />
                        </button>
                      </>
                    )}
                  </motion.article>
                ))}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredEpisodes.length === 0 && (
            <div className="py-20 text-center">
              <Mic className="w-12 h-12 mx-auto text-gray-300" />
              <p className="mt-4 text-gray-500">Aucun épisode trouvé</p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-sm text-rose-valley-600 hover:underline"
                >
                  Effacer la recherche
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Episode Player Modal */}
      <AnimatePresence>
        {selectedEpisode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedEpisode(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl overflow-hidden bg-white shadow-2xl rounded-2xl"
            >
              <div className="relative aspect-video bg-gray-900">
                {selectedEpisode.collectionViewUrl ? (
                  <iframe
                    src={`https://embed.podcasts.apple.com/podcast/id${ecosystem.media.podcast.applePodcastId}?i=${selectedEpisode.trackId}&theme=light`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full">
                    <div className="text-center">
                      <Mic className="w-16 h-16 mx-auto text-gray-600" />
                      <p className="mt-4 text-gray-400">Lecteur audio</p>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => setSelectedEpisode(null)}
                  className="absolute p-2 transition-colors bg-black/50 rounded-full top-4 right-4 text-white hover:bg-black/70"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedEpisode.trackName || selectedEpisode.title}
                </h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <span>{formatDate(selectedEpisode.releaseDate)}</span>
                  <span>{formatDuration(selectedEpisode.trackTimeMillis)}</span>
                </div>
                <p className="mt-4 text-sm text-gray-600 line-clamp-3">
                  {selectedEpisode.description?.replace(/<[^>]*>/g, '')}
                </p>
                
                <div className="flex items-center gap-3 mt-6">
                  <a
                    href={selectedEpisode.collectionViewUrl || podcast.platforms.apple}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Apple Podcasts
                  </a>
                  <a
                    href={podcast.platforms.spotify}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    <Headphones className="w-4 h-4 mr-2" />
                    Spotify
                  </a>
                  <a
                    href={podcast.platforms.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    <Youtube className="w-4 h-4 mr-2" />
                    YouTube
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  )
}
